/**
 * プライバシーポリシー（公開ページ・ログイン不要）
 *
 * 本文の出典: docs/OWNER_APP_LEGAL_DRAFT.md 第 2 部
 *
 * 注意:
 * - 法的に有効な「正本」は日本語。UI 言語に関わらず本文は日本語で表示する。
 *   英語ロケール時はページ冒頭に「正本は日本語版」の旨だけ案内。
 * - 雛形ドラフト中の ◯ プレースホルダは、暫定値（5 ヶ月 / 5 年 / 1 年 等）で
 *   埋めている。法務確認後に確定値で置換する必要あり。
 * - 連絡先 (support@naturenix.co.jp) や本社所在地は確定値で記載済。
 *   変更があれば下記の定数で一括差替え可能。
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const COMPANY_NAME = "株式会社 Naturenix";
const SUPPORT_EMAIL = "support@naturenix.co.jp";
const PRIVACY_EMAIL = "privacy@naturenix.co.jp";
const LAST_UPDATED = "2026-04-22";

export default function PrivacyPage() {
  const { i18n, t } = useTranslation();
  const isJa = i18n.language?.startsWith("ja") ?? true;

  return (
    <article className="nx-legal">
      <header className="nx-legal__header">
        <Link to="/login" className="nx-btn-ghost nx-legal__back">
          ← {t("common.back")}
        </Link>
        <h1>プライバシーポリシー</h1>
        <p className="nx-text-secondary nx-legal__meta">最終更新: {LAST_UPDATED}</p>
        {!isJa && (
          <p className="nx-legal__notice">
            This page is the official Japanese version of our Privacy Policy.
            An English translation is not provided; please consult a Japanese-speaker
            if needed. Application UI is bilingual but the legal text is Japanese.
          </p>
        )}
      </header>

      <section>
        <h2>第 1 条（基本方針）</h2>
        <p>
          {COMPANY_NAME}（以下「当社」）は、利用者の個人情報の重要性を認識し、
          個人情報保護法その他の関連法令を遵守し、適切に取り扱います。
        </p>
      </section>

      <section>
        <h2>第 2 条（取得する情報）</h2>

        <h3>2.1 アカウント登録時に取得する情報</h3>
        <ul>
          <li>氏名（個人事業主の場合）／会社名</li>
          <li>担当者氏名</li>
          <li>電子メールアドレス</li>
          <li>電話番号（事業所・携帯）</li>
          <li>住所（郵便番号・都道府県・市区町村）</li>
          <li>販売代理店情報</li>
        </ul>
        <p className="nx-text-secondary">
          ※ これらは保証登録時に既に取得済みの情報を本サービスでも利用するものです。
        </p>

        <h3>2.2 サービス利用に伴って取得する情報</h3>
        <ul>
          <li>
            対象機器のテレメトリ
            <ul>
              <li>充電残量（SoC）</li>
              <li>電圧、電流、温度</li>
              <li>ポートの ON/OFF 状態</li>
              <li>サイクル数（充放電回数）</li>
              <li>エラー・障害情報</li>
              <li>
                <strong>
                  対象機器の概略位置情報（市区町村レベル。WiFi スキャン情報をもとに当社サーバーで推定）
                </strong>
              </li>
            </ul>
          </li>
          <li>
            アプリ利用ログ
            <ul>
              <li>ログイン日時</li>
              <li>IP アドレス</li>
              <li>ユーザーエージェント（ブラウザ・OS の情報）</li>
            </ul>
          </li>
          <li>
            プッシュ通知購読情報
            <ul>
              <li>プッシュサービスのエンドポイント URL またはデバイストークン</li>
              <li>購読端末の識別情報</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2>第 3 条（利用目的）</h2>
        <p>当社は、取得した情報を以下の目的で利用します。</p>
        <ol>
          <li>本サービスの提供（対象機器の稼働状態の表示、通知の送付）</li>
          <li>不具合検知・予防保全</li>
          <li>製品改善のための統計分析（個人を特定できない集計形式に限る）</li>
          <li>保証管理・カスタマーサポート</li>
          <li>重要なお知らせの通知</li>
          <li>本サービスの利用状況の分析および機能改善</li>
        </ol>
      </section>

      <section>
        <h2>第 4 条（第三者提供）</h2>
        <ol>
          <li>
            当社は、利用者の個人情報を<strong>第三者に提供しません</strong>。
            ただし、以下の場合を除きます。
            <ul>
              <li>利用者の同意がある場合</li>
              <li>法令に基づく場合</li>
              <li>人の生命・身体・財産の保護のために必要があり、利用者の同意取得が困難な場合</li>
              <li>
                当社の販売代理店、修理委託先、または当社のサービス提供に必要な業務委託先に対し、
                業務遂行に必要な範囲で開示する場合（当社は委託先に対し適切な監督を行います）
              </li>
            </ul>
          </li>
        </ol>
      </section>

      <section>
        <h2>第 5 条（保管期間）</h2>
        <ol>
          <li>
            <strong>アカウント情報</strong>: 本サービスの利用期間中および退会後 5 年間
          </li>
          <li>
            <strong>テレメトリ（生データ）</strong>: 受信から約 5 ヶ月
          </li>
          <li>
            <strong>テレメトリ（日次集計）</strong>: 受信から 5 年間
          </li>
          <li>
            <strong>アプリ利用ログ</strong>: 12 ヶ月
          </li>
          <li>
            <strong>退会後</strong>: 氏名・電子メールアドレス等の個人特定情報は速やかに匿名化または削除します。
            テレメトリは個人を特定できない集計データに変換して保持することがあります。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 6 条（認証情報の取り扱い）</h2>
        <ol>
          <li>
            本サービスはパスワードを使用しません。電子メールアドレス宛に送付する 6 桁のワンタイムパスワード（OTP）
            によって認証を行います。
          </li>
          <li>OTP は 5 分以内に有効期限が切れます。</li>
          <li>
            認証後、利用者の端末に発行される認証トークン（JWT）は <strong>30 日間有効</strong>です。
            トークンは利用者の端末のローカルストレージに保存されます。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 7 条（Cookie および類似技術の利用）</h2>
        <ol>
          <li>
            本サービスは、認証トークンの保管のため、利用者の端末のブラウザのローカルストレージを使用します。
            これは Cookie とは異なる技術ですが、機能的には類似します。
          </li>
          <li>
            プッシュ通知機能を有効にした場合、Service Worker（ブラウザのバックグラウンドで動作するスクリプト）
            またはネイティブアプリのプッシュ登録を利用者の端末に登録します。
          </li>
          <li>
            利用者は、ブラウザの設定または OS の通知設定によりこれらを無効化できますが、
            その場合本サービスの一部機能が利用できなくなることがあります。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 8 条（プッシュ通知）</h2>
        <ol>
          <li>
            利用者がプッシュ通知の受信に同意した場合、当社は対象機器に異常が検知された場合等にプッシュ通知を送信します。
          </li>
          <li>
            利用者はいつでもアプリ内または OS の通知設定からプッシュ通知の受信を停止できます。
          </li>
          <li>
            プッシュ通知は、Apple Push Notification service (APNs)、Firebase Cloud Messaging (FCM)、
            または Web Push 規格のプッシュサービスを経由して配信されます。配信過程で通信内容は暗号化されますが、
            配信先サーバーの運営者がメタデータ（送信先・タイミング等）を取得する可能性があります。
          </li>
        </ol>
      </section>

      <section>
        <h2>第 9 条（位置情報）</h2>
        <ol>
          <li>
            本サービスにおいて表示される対象機器の位置情報は、<strong>市区町村レベルの概略位置</strong>です。
          </li>
          <li>
            位置情報は、対象機器が検出した周辺の WiFi アクセスポイント情報をもとに当社サーバーで推定したものであり、
            GPS による正確な座標ではありません。
          </li>
          <li>本サービスは利用者の端末（スマートフォン等）の位置情報は取得しません。</li>
        </ol>
      </section>

      <section>
        <h2>第 10 条（情報の安全管理）</h2>
        <p>当社は、取得した情報の漏洩・改ざん・紛失を防ぐため、以下の措置を講じます。</p>
        <ol>
          <li>通信は HTTPS により暗号化</li>
          <li>データベースへのアクセス制御</li>
          <li>認証トークンの定期的な失効</li>
          <li>従業員に対する個人情報保護教育</li>
          <li>委託先の選定および監督</li>
          <li>インシデント発生時の連絡体制の整備</li>
        </ol>
      </section>

      <section>
        <h2>第 11 条（個人情報の開示・訂正・削除請求）</h2>
        <ol>
          <li>
            利用者は、当社に対し、以下を請求することができます。
            <ul>
              <li>自己の個人情報の開示</li>
              <li>内容の訂正・追加・削除</li>
              <li>利用停止</li>
              <li>第三者提供の停止</li>
            </ul>
          </li>
          <li>
            請求方法: 当社サポート窓口（<a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>）
            まで電子メールにてご連絡ください。
          </li>
          <li>当社は、本人確認を行ったうえで、合理的な期間内に対応します。</li>
        </ol>
      </section>

      <section>
        <h2>第 12 条（海外への情報移転）</h2>
        <ol>
          <li>
            本サービスのインフラは、原則として日本国内のクラウドサービスで提供します。
            業務委託先の選定により一部の処理が日本国外で行われる場合は、適切な保護措置を講じたうえで実施します。
          </li>
          <li>本サービスの提供にあたり、利用者の情報が日本国外に保管される場合があります。</li>
        </ol>
      </section>

      <section>
        <h2>第 13 条（提供地域）</h2>
        <p>
          本サービスは<strong>日本国内のオーナーを対象</strong>としています。
          日本国外からのアクセスは制限することがあります。英語版 UI は日本在住の英語話者の便宜のために
          提供するものであり、日本国外への提供を意図するものではありません。
        </p>
      </section>

      <section>
        <h2>第 14 条（プライバシーポリシーの変更）</h2>
        <p>
          当社は、必要と判断した場合、本ポリシーを変更することがあります。重要な変更については、
          本サービスにて事前に通知します。
        </p>
      </section>

      <section>
        <h2>第 15 条（お問い合わせ窓口）</h2>
        <p>本ポリシーに関するお問い合わせ、開示請求等は以下までご連絡ください。</p>
        <address className="nx-legal__address">
          {COMPANY_NAME} 個人情報お問い合わせ窓口<br />
          電子メール: <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a><br />
          サポート全般: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </address>
      </section>

      <footer className="nx-legal__footer">
        <Link to="/terms" className="nx-btn-ghost">利用規約を見る →</Link>
        <Link to="/login" className="nx-btn-ghost">ログイン画面に戻る</Link>
      </footer>
    </article>
  );
}
